import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { FieldCreateDTO } from '../../../types/fields';

export interface ImportDialogResult {
  action: 'import' | 'cancel';
  fields?: FieldCreateDTO[];
}

@Component({
  selector: 'app-import-dialog',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30">
            <mat-icon class="text-blue-600">upload</mat-icon>
          </div>
          <div>
            <h2 data-testid="import-dialog-heading" class="text-lg font-semibold text-zinc-900 dark:text-white">Import Fields</h2>
            <p class="text-sm text-zinc-500 dark:text-zinc-400">Paste JSON to import fields</p>
          </div>
        </div>
        <button
          mat-icon-button
          (click)="onCancel()"
          class="!-mr-2 !-mt-2"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- JSON Input -->
      <div class="space-y-2">
        <label class="text-sm font-medium text-zinc-700 dark:text-zinc-300">JSON Data</label>
        <textarea
          data-testid="import-dialog-json-input"
          [(ngModel)]="jsonInput"
          (ngModelChange)="validateJson()"
          placeholder='[
  {
    "name": "fieldName",
    "category": "Invoice",
    "type": "string",
    "required": true,
    "isAlwaysVisible": true,
    "description": "Field description",
    "enumValues": [],
    "defaultValue": null
  }
]'
          class="w-full h-64 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-2 text-sm font-mono shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none dark:placeholder:text-zinc-500"
        ></textarea>

        @if (errorMessage()) {
          <div class="flex items-center gap-2 text-red-600 text-sm">
            <mat-icon class="!text-base !w-4 !h-4 !leading-4">error</mat-icon>
            <span>{{ errorMessage() }}</span>
          </div>
        }

        @if (parsedFields().length > 0 && !errorMessage()) {
          <div class="flex items-center gap-2 text-green-600 text-sm">
            <mat-icon class="!text-base !w-4 !h-4 !leading-4">check_circle</mat-icon>
            <span>{{ parsedFields().length }} field(s) ready to import</span>
          </div>
        }
      </div>

      <!-- Paste from Clipboard Button -->
      <div class="mt-4">
        <button
          data-testid="import-dialog-paste-btn"
          mat-stroked-button
          (click)="pasteFromClipboard()"
          class="w-full"
        >
          <mat-icon>content_paste</mat-icon>
          <span class="ml-2">Paste from Clipboard</span>
        </button>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-zinc-100 dark:border-zinc-700">
        <button data-testid="import-dialog-cancel-btn" mat-stroked-button (click)="onCancel()">
          Cancel
        </button>
        <button
          data-testid="import-dialog-import-btn"
          mat-flat-button
          color="primary"
          [disabled]="parsedFields().length === 0 || !!errorMessage()"
          (click)="onImport()"
        >
          <mat-icon>upload</mat-icon>
          <span class="ml-1">Import {{ parsedFields().length }} Field(s)</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class ImportDialogComponent {
  private dialogRef = inject(MatDialogRef<ImportDialogComponent>);

  jsonInput = '';
  errorMessage = signal<string>('');
  parsedFields = signal<FieldCreateDTO[]>([]);

  validateJson(): void {
    if (!this.jsonInput.trim()) {
      this.errorMessage.set('');
      this.parsedFields.set([]);
      return;
    }

    try {
      const parsed = JSON.parse(this.jsonInput);
      const fields = Array.isArray(parsed) ? parsed : [parsed];

      // Validate each field
      const validFields: FieldCreateDTO[] = [];
      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        const validation = this.validateField(field, i);
        if (validation.error) {
          this.errorMessage.set(validation.error);
          this.parsedFields.set([]);
          return;
        }
        validFields.push(validation.field!);
      }

      this.errorMessage.set('');
      this.parsedFields.set(validFields);
    } catch (e) {
      this.errorMessage.set('Invalid JSON format');
      this.parsedFields.set([]);
    }
  }

  private validateField(field: any, index: number): { error?: string; field?: FieldCreateDTO } {
    const prefix = `Field ${index + 1}: `;

    if (!field || typeof field !== 'object') {
      return { error: `${prefix}Must be an object` };
    }

    if (!field.name || typeof field.name !== 'string') {
      return { error: `${prefix}Missing or invalid 'name' (string required)` };
    }

    if (!field.category || typeof field.category !== 'string' || !field.category.trim()) {
      return { error: `${prefix}Missing or invalid 'category' (non-empty string required)` };
    }

    const validTypes = ['string', 'number', 'boolean', 'string-list', 'number-list', 'object', 'object-list'];
    if (!field.type || !validTypes.includes(field.type)) {
      return { error: `${prefix}Invalid 'type'. Must be one of: ${validTypes.join(', ')}` };
    }

    const isObjectType = field.type === 'object' || field.type === 'object-list';

    // Build the validated field with defaults
    const validField: FieldCreateDTO = {
      ...(field.id && typeof field.id === 'string' ? { id: field.id } : {}),
      name: field.name.trim(),
      category: field.category.trim(),
      type: field.type,
      required: typeof field.required === 'boolean' ? field.required : false,
      isAlwaysVisible: typeof field.isAlwaysVisible === 'boolean' ? field.isAlwaysVisible : false,
      defaultValue: isObjectType ? null : (field.defaultValue ?? null),
      description: field.description || '',
      enumValues: Array.isArray(field.enumValues) ? field.enumValues : [],
      ...(isObjectType && Array.isArray(field.fieldIds) ? { fieldIds: field.fieldIds } : {}),
      // minTolerance is only meaningful for anchor fields and is fully optional on import.
      // Must be a number in [0, 1] with at most 2 decimal digits.
      ...(typeof field.minTolerance === 'number'
        && field.category?.trim() === 'anchor'
        && field.minTolerance >= 0
        && field.minTolerance <= 1
        && Math.abs(field.minTolerance * 100 - Math.round(field.minTolerance * 100)) < 1e-9
        ? { minTolerance: field.minTolerance }
        : {})
    };

    return { field: validField };
  }

  async pasteFromClipboard(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      this.jsonInput = text;
      this.validateJson();
    } catch (e) {
      this.errorMessage.set('Unable to read from clipboard. Please paste manually.');
    }
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' } as ImportDialogResult);
  }

  onImport(): void {
    if (this.parsedFields().length > 0) {
      this.dialogRef.close({
        action: 'import',
        fields: this.parsedFields()
      } as ImportDialogResult);
    }
  }
}
